import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { KeenIcon } from '@/components/keenicons';
import { useCreateRole } from '@/hooks/admin/useRoles';
import type { CreateRoleRequest } from '@/services/admin/role.types';
import { toast } from 'sonner';

/**
 * Validation schema for role creation
 *
 * Matches backend validation rules:
 * - Name: Required, 1-256 characters, alphanumeric with spaces/hyphens/underscores
 * - Description: Optional, max 500 characters
 * - isDefault: Optional boolean
 * - isPublic: Optional boolean
 */
const createRoleSchema = z.object({
  name: z
    .string()
    .min(1, 'Role name is required')
    .max(256, 'Role name cannot exceed 256 characters')
    .regex(
      /^[a-zA-Z0-9_\-\s.]+$/,
      'Role name can only contain letters, numbers, spaces, hyphens, underscores, and dots'
    ),
  description: z
    .string()
    .max(500, 'Description cannot exceed 500 characters')
    .optional(),
  isDefault: z.boolean().optional(),
  isPublic: z.boolean().optional(),
});

type CreateRoleFormData = z.infer<typeof createRoleSchema>;

interface CreateRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRoleCreated: () => void;
}

/**
 * Create Role Dialog Component
 *
 * Modal dialog for creating a new role
 *
 * Features:
 * - Form validation with Zod
 * - Real-time validation feedback
 * - Loading states
 * - Error handling
 * - Success notifications
 *
 * Spec reference: Lines 587-740 in 03-AUTH-AUTHORIZATION-SPECS.md
 */
export function CreateRoleDialog({
  open,
  onOpenChange,
  onRoleCreated,
}: CreateRoleDialogProps) {
  const createRoleMutation = useCreateRole();

  const form = useForm<CreateRoleFormData>({
    resolver: zodResolver(createRoleSchema),
    defaultValues: {
      name: '',
      description: '',
      isDefault: false,
      isPublic: false,
    },
  });

  const onSubmit = async (data: CreateRoleFormData) => {
    try {
      const requestData: CreateRoleRequest = {
        name: data.name.trim(),
        isDefault: data.isDefault,
        isPublic: data.isPublic,
        extraProperties: {
          description: data.description?.trim() || '',
        },
      };

      const newRole = await createRoleMutation.mutateAsync(requestData);

      toast.success('Role created successfully', {
        description: `The role "${newRole.name}" has been created.`,
      });

      // Reset form and close dialog
      form.reset();
      onRoleCreated();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
      toast.error('Failed to create role', {
        description: message,
      });
    }
  };

  const handleClose = () => {
    if (createRoleMutation.isPending) return; // Prevent closing during submission
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeenIcon icon="shield-plus" className="text-xl" />
            Create New Role
          </DialogTitle>
          <DialogDescription>
            Create a new role and define its properties. You can assign permissions after
            creation.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Role Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Role Name <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., Moderator, Editor, Contributor"
                      {...field}
                      disabled={createRoleMutation.isPending}
                    />
                  </FormControl>
                  <FormDescription>
                    A unique name to identify this role. Use alphanumeric characters only.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe the purpose and responsibilities of this role"
                      rows={3}
                      {...field}
                      disabled={createRoleMutation.isPending}
                    />
                  </FormControl>
                  <FormDescription>
                    Optional description to explain the role's purpose.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Options */}
            <div className="space-y-4 rounded-lg border p-4">
              <h4 className="text-sm font-semibold">Role Options</h4>

              {/* Is Default */}
              <FormField
                control={form.control}
                name="isDefault"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={createRoleMutation.isPending}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Set as default role</FormLabel>
                      <FormDescription>
                        Automatically assign this role to new users upon registration.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              {/* Is Public */}
              <FormField
                control={form.control}
                name="isPublic"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={createRoleMutation.isPending}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Make publicly available</FormLabel>
                      <FormDescription>
                        Allow users to see and potentially request this role.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            </div>

            {/* Footer Actions */}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={createRoleMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createRoleMutation.isPending} className="gap-2">
                {createRoleMutation.isPending && (
                  <KeenIcon icon="arrows-circle" className="animate-spin" />
                )}
                Create Role
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
